// WHAT A FAILED REQUEST IS ALLOWED TO SAY TO A PERSON.
//
// Two things used to reach the screen verbatim and neither of them is a sentence:
//
//   1. THE WIRE FORMAT. `httpApi.toError` builds `"<status> <detail>"` so a log
//      line carries the code, and `useApi` handed that straight to a notice —
//      "409 That serial is held by ABC Electrical". The code is for the developer;
//      the detail is for the reader. Strip the one, keep the other.
//   2. `Response.statusText`. When the cloud answers with no JSON body at all
//      (a gateway, a proxy, an unhandled 500) the "detail" is the bare HTTP reason
//      phrase — "Forbidden", "Bad Gateway" — which tells a homeowner nothing and
//      reads like a system fault they caused. A reason phrase is NOT a message,
//      and this module refuses to present it as one.
//
// The rule this serves is the repo's: a screen must never present absent, stale or
// failed data as current, device-sourced or saved. A failure that renders as an
// empty string, or as a word nobody outside HTTP recognises, is a failure the
// reader cannot act on — so every path here ends in a sentence.

/** The shape `httpApi` throws: an `Error` carrying the HTTP status, and — for a
 *  request that never got an answer at all — a `code` naming why. */
export interface ApiError extends Error {
  status?: number
  code?: 'timeout' | 'network'
}

/** A request that was abandoned because the server did not answer in time. The
 *  distinction matters: a timeout is NOT a refusal, so the caller cannot say the
 *  write did not happen — only that it has no answer. */
export const TIMEOUT_MESSAGE =
  'The server did not answer in time. It is not known whether this was applied — check the current state before retrying.'

export const NETWORK_MESSAGE =
  'We could not reach the server. Check your connection and try again.'

export function isTimeoutError(e: unknown): boolean {
  return (e as ApiError | null)?.code === 'timeout'
}

/** Build the timeout error `raw()` raises, so callers can branch on it by code
 *  rather than by matching prose. */
export function timeoutError(afterMs: number): ApiError {
  const e = new Error(TIMEOUT_MESSAGE) as ApiError
  e.code = 'timeout'
  e.name = 'TimeoutError'
  ;(e as ApiError & { afterMs: number }).afterMs = afterMs
  return e
}

export function networkError(): ApiError {
  const e = new Error(NETWORK_MESSAGE) as ApiError
  e.code = 'network'
  e.name = 'NetworkError'
  return e
}

/** Every HTTP reason phrase we might be handed as `statusText`. Matching one of
 *  these means the server sent NO message of its own — there is nothing to quote. */
const REASON_PHRASES = new Set([
  'ok', 'created', 'accepted', 'no content',
  'bad request', 'unauthorized', 'unauthorised', 'payment required', 'forbidden', 'not found',
  'method not allowed', 'not acceptable', 'request timeout', 'conflict', 'gone',
  'length required', 'precondition failed', 'payload too large', 'content too large',
  'uri too long', 'unsupported media type', 'range not satisfiable', 'expectation failed',
  'unprocessable entity', 'unprocessable content', 'locked', 'failed dependency',
  'too early', 'upgrade required', 'precondition required', 'too many requests',
  'request header fields too large', 'unavailable for legal reasons',
  'internal server error', 'not implemented', 'bad gateway', 'service unavailable',
  'gateway timeout', 'http version not supported', 'insufficient storage',
  // Fetch's own non-answers, which are not messages either.
  'failed to fetch', 'network error', 'networkerror', 'load failed', 'fetch failed',
  'error', 'unknown error', 'signed out',
])

/** The sentence a status code earns when the server offered none. */
function sentenceForStatus(status: number | undefined, fallback: string): string {
  if (status === undefined) return fallback
  if (status === 401) return 'Your session has ended. Sign in again to continue.'
  if (status === 403) return 'Your account is not permitted to do that.'
  if (status === 404) return 'That is no longer there. Go back and reload before trying again.'
  if (status === 408) return TIMEOUT_MESSAGE
  if (status === 409) return 'Something else changed this first. Reload to see the current state, then try again.'
  if (status === 429) return 'Too many requests just now. Wait a moment and try again.'
  if (status === 400 || status === 422) return 'The server would not accept that. Check the details and try again.'
  if (status >= 500) return 'The server could not complete that request. Nothing here is confirmed — try again shortly.'
  return fallback
}

/**
 * The one place a thrown API error becomes text a person reads.
 *
 * `fallback` is the CALLER's sentence for "the server said nothing useful" — it
 * should name what was being attempted ("That declaration could not be
 * withdrawn."), because the status sentence above cannot know.
 */
export function describeApiError(e: unknown, fallback = 'Something went wrong — please try again.'): string {
  if (e == null) return fallback
  const err = e as ApiError
  if (err.code === 'timeout') return TIMEOUT_MESSAGE
  if (err.code === 'network') return NETWORK_MESSAGE

  const raw = typeof err.message === 'string' && err.message ? err.message : String(e)
  // "409 That serial is held by…" → "That serial is held by…"; a leading code with
  // nothing after it → nothing at all.
  const stripped = raw.replace(/^\s*\d{3}\s*/, '').trim()
  const status = typeof err.status === 'number'
    ? err.status
    : /^\s*(\d{3})\b/.exec(raw)
      ? Number(/^\s*(\d{3})\b/.exec(raw)![1])
      : undefined

  if (!stripped || REASON_PHRASES.has(stripped.toLowerCase())) return sentenceForStatus(status, fallback)
  // Spec/trace IDs (APP-*, CLD-*, …) are for engineers — never product copy.
  const clean = stripRequirementIds(stripped)
  if (!clean || REASON_PHRASES.has(clean.toLowerCase())) return sentenceForStatus(status, fallback)
  return clean
}

/** Drop parentheticals whose content is primarily a requirements / ICD id. */
export function stripRequirementIds(text: string): string {
  return text
    .replace(/\s*\((?:[^()]*\b(?:APP|CLD|SYS|GRI|ICD|HEM|MOD-TBR|DNG)-[A-Z0-9]+[-A-Z0-9/]*)[^()]*\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([.,;:!?])/g, '$1')
    .trim()
}
